import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { StatusBadge } from "@/components/Badges";
import ClickableImage from "@/components/ClickableImage";
import { toast } from "sonner";
import { Building2, Check, X, MapPin, Phone, Trash2 } from "lucide-react";

export default function AdminCompanies() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/companies", { params: filter === "all" ? {} : { status: filter } })
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);

  const act = async (id, action) => {
    try {
      if (action === "delete") await api.delete(`/admin/users/${id}`);
      else await api.post(`/admin/companies/${id}/${action}`);
      toast.success(action === "approve" ? "Đã duyệt nhà xe" : action === "reject" ? "Đã từ chối" : "Đã xóa");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const tabCls = (t) => `px-4 py-2 text-sm font-semibold rounded-sm transition-colors ${filter === t ? "bg-primary text-white" : "text-zinc-400 hover:text-white border border-zinc-800"}`;

  return (
    <div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-white flex items-center gap-3">
        <Building2 className="w-9 h-9 text-primary" /> Duyệt nhà xe
      </h1>
      <p className="text-zinc-500 mt-2 mb-6">Duyệt tài khoản đăng ký của các nhà xe.</p>

      <div className="flex gap-2 mb-6">
        <button data-testid="filter-pending" onClick={() => setFilter("pending")} className={tabCls("pending")}>Chờ duyệt</button>
        <button data-testid="filter-approved" onClick={() => setFilter("approved")} className={tabCls("approved")}>Đã duyệt</button>
        <button data-testid="filter-rejected" onClick={() => setFilter("rejected")} className={tabCls("rejected")}>Từ chối</button>
        <button data-testid="filter-all" onClick={() => setFilter("all")} className={tabCls("all")}>Tất cả</button>
      </div>

      {loading ? (
        <p className="text-zinc-600 py-10 text-center">Đang tải...</p>
      ) : rows.length === 0 ? (
        <p className="text-zinc-600 py-10 text-center">Không có nhà xe nào.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {rows.map((c) => (
            <div key={c.id} data-testid={`company-${c.id}`} className="bg-card border border-zinc-800 rounded-sm overflow-hidden">
              {c.proof_image && (
                <ClickableImage path={c.proof_image} label={c.company_name} className="w-full aspect-video object-cover border-b border-zinc-800" />
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-display font-bold text-white text-lg">{c.company_name}</div>
                  <StatusBadge status={c.status} />
                </div>
                <div className="space-y-1 text-sm text-zinc-400 mb-4">
                  <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-zinc-600" /> {c.phone}</div>
                  <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-zinc-600" /> {c.address}</div>
                  <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-zinc-600" /> Khu vực: {c.area}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {c.status !== "approved" && (
                    <button data-testid={`approve-${c.id}`} onClick={() => act(c.id, "approve")} className="flex items-center gap-1 bg-green-600/90 hover:bg-green-600 text-white rounded-sm px-3 py-2 text-sm font-semibold transition-colors">
                      <Check className="w-4 h-4" /> Duyệt
                    </button>
                  )}
                  {c.status === "pending" && (
                    <button data-testid={`reject-${c.id}`} onClick={() => act(c.id, "reject")} className="flex items-center gap-1 border border-zinc-700 text-zinc-300 hover:text-white rounded-sm px-3 py-2 text-sm transition-colors">
                      <X className="w-4 h-4" /> Từ chối
                    </button>
                  )}
                  <button data-testid={`delete-company-${c.id}`} onClick={() => act(c.id, "delete")} className="flex items-center gap-1 bg-red-950 text-red-500 border border-red-900 hover:bg-red-900 hover:text-white rounded-sm px-3 py-2 text-sm transition-colors">
                    <Trash2 className="w-4 h-4" /> Xóa
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
